/**
 * The exit chapter's market and season — usability audit C9 (2026-10-02).
 *
 * Every exit read was issued estate-wide (`query: {}`), with no way to ask for one market or one
 * season, while the platform's reads take both: `country_key` on every book read, `season_code`
 * where a row belongs to a season (the plans, the unsigned plans, the terminal stock, the D4
 * evidence). This is the chapter's one perimeter for them:
 *
 *   `?market=`   one market — sent as `country_key` to every read that takes it
 *   `?season=`   one season — sent as `season_code` to the reads that take it; a page whose reads
 *                take none does not offer it (a season it could not apply would be a claim)
 *
 * **No default.** No market is every market the caller may read; no season is every season. The
 * choices are the markets and seasons the plan book names (`GET /exit/plans`, unfiltered — the
 * register of what is being exited), the linked value always kept, so a link to a market the book
 * no longer holds still shows what it asks for and reads empty.
 *
 * **An empty filtered read says so.** A filter that matches nothing is not the job that has not
 * run: the pages' empty sentence becomes «nothing for this market / season» while a filter is set.
 *
 * The file of every page states the perimeter: `country_key` / `season_code` `equals` the value,
 * in place of the `not-applied` the sheets wrote while no read was ever narrowed.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiCollection, useSeasonLabel } from '@/api';
import { exitListPlans } from '@/api/generated';
import type { ExportRequest, ProvenanceFilter } from '@/export';
import { isAbsent } from '@/export';
import { Button, Select } from '@/primitives';

import { setExitScopeParam } from './scope';

export const MARKET_PARAM = 'market';
export const SEASON_PARAM = 'season';

/** Radix forbids `''` as a value: the "no narrowing" choice. */
const ALL = '__all__';

export interface MarketSeason {
  readonly market: string | null;
  readonly season: string | null;
}

export const NO_MARKET_SEASON: MarketSeason = { market: null, season: null };

/** The market and season a link names. Pure. */
export function readMarketSeason(search: string): MarketSeason {
  const params = new URLSearchParams(search);
  const read = (key: string): string | null => {
    const value = params.get(key)?.trim() ?? '';
    return value === '' ? null : value;
  };
  return { market: read(MARKET_PARAM), season: read(SEASON_PARAM) };
}

/** Whether either narrows the reads. Pure. */
export function scoped(scope: MarketSeason, withSeason = true): boolean {
  return scope.market !== null || (withSeason && scope.season !== null);
}

/** `country_key` for a read that takes only the market. Pure. */
export function marketQuery(scope: MarketSeason): { country_key?: string } {
  return scope.market === null ? {} : { country_key: scope.market };
}

/** `country_key` and `season_code` for a read that takes both. Pure. */
export function marketSeasonQuery(scope: MarketSeason): { country_key?: string; season_code?: string } {
  return { ...marketQuery(scope), ...(scope.season === null ? {} : { season_code: scope.season }) };
}

/**
 * `request` with the perimeter it was read under: `country_key` (and, where the read takes it,
 * `season_code`) `equals` the value, replacing the sheet's `not-applied` line for it. A declared
 * absence of the filter list stays declared. Pure.
 */
export function withMarketSeason(request: ExportRequest, scope: MarketSeason, withSeason: boolean): ExportRequest {
  const applied: ProvenanceFilter[] = [
    ...(scope.market === null ? [] : [{ field: 'country_key', operator: 'equals', value: scope.market }]),
    ...(withSeason && scope.season !== null ? [{ field: 'season_code', operator: 'equals', value: scope.season }] : []),
  ];
  if (applied.length === 0) return request;
  const fields = new Set(applied.map((filter) => filter.field));
  const merge = (list: readonly ProvenanceFilter[]): readonly ProvenanceFilter[] => [
    ...list.filter((filter) => !fields.has(filter.field)),
    ...applied,
  ];
  const stated = request.provenance.filters;
  return {
    ...request,
    provenance: { ...request.provenance, filters: isAbsent(stated) ? stated : merge(stated) },
    perimeter: { ...request.perimeter, filters: merge(request.perimeter.filters) },
  };
}

/** The market and season of the current address, following back and forward. */
export function useMarketSeason(): MarketSeason {
  const [search, setSearch] = useState(() => (typeof window === 'undefined' ? '' : window.location.search));
  useEffect(() => {
    const onPopState = (): void => setSearch(window.location.search);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  return useMemo(() => readMarketSeason(search), [search]);
}

/** The market and season of an export's scope, else of the address bar. */
export function exportMarketSeason(given?: { readonly search: string }): MarketSeason {
  return readMarketSeason(given?.search ?? (typeof window === 'undefined' ? '' : window.location.search));
}

/** The empty sentence of a filtered read: nothing for this market / season — not a job that never ran. */
export function filteredEmpty(scope: MarketSeason): { readonly key: string; readonly values: Readonly<Record<string, string>> } {
  return {
    key: 'exit.scope.emptyFiltered',
    values: { market: scope.market ?? '', season: scope.season ?? '', hasMarket: scope.market === null ? 'no' : 'yes', hasSeason: scope.season === null ? 'no' : 'yes' },
  };
}

/**
 * The market (and season) choosers, at the head of an exit page. `season: false` on a page whose
 * reads take no season. The choices are the plan book's markets and seasons.
 */
export function MarketSeasonBar({ season = true }: { readonly season?: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  const seasonLabel = useSeasonLabel();
  const scope = useMarketSeason();
  const book = useApiCollection(exitListPlans, { query: {} });
  const markets = useMemo(() => {
    const set = new Set((book.data ?? []).map((plan) => plan.country_key));
    if (scope.market !== null) set.add(scope.market);
    return [...set].filter((value) => value !== '').sort();
  }, [book.data, scope.market]);
  const seasons = useMemo(() => {
    const set = new Set((book.data ?? [])
      .filter((plan) => scope.market === null || plan.country_key === scope.market)
      .map((plan) => plan.season_code));
    if (scope.season !== null) set.add(scope.season);
    return [...set].filter((value) => value !== '').sort();
  }, [book.data, scope.market, scope.season]);
  const any = scope.market !== null || (season && scope.season !== null);
  return (
    <div role="group" aria-label={t('exit.scope.label')} data-exit-scope="" className="flex flex-wrap items-end gap-3">
      <div className="max-w-xs" data-exit-market="">
        <Select
          labelKey="exit.scope.market"
          options={[{ value: ALL, label: t('exit.scope.allMarkets') }, ...markets.map((value) => ({ value, label: value }))]}
          value={scope.market ?? ALL}
          onValueChange={(value) => setExitScopeParam(MARKET_PARAM, value === ALL ? null : value)}
        />
      </div>
      {season && (
        <div className="max-w-xs" data-exit-season="">
          <Select
            labelKey="exit.scope.season"
            options={[{ value: ALL, label: t('exit.scope.allSeasons') }, ...seasons.map((value) => ({ value, label: seasonLabel(value) }))]}
            value={scope.season ?? ALL}
            onValueChange={(value) => setExitScopeParam(SEASON_PARAM, value === ALL ? null : value)}
          />
        </div>
      )}
      {any && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExitScopeParam(MARKET_PARAM, null, { clear: [SEASON_PARAM] })}
        >
          {t('exit.scope.clear')}
        </Button>
      )}
    </div>
  );
}

/**
 * The page strip's empty sentence: the page's own (the job that has not run) while nothing is
 * filtered, else «nothing for this market / season» — and no job named, since none is missing.
 */
export function emptyStrip(
  scope: MarketSeason, withSeason: boolean, missingKey: string, source?: string,
): { readonly missingKey: string; readonly missingValues?: Readonly<Record<string, string>>; readonly source?: string } {
  if (!scoped(scope, withSeason)) return { missingKey, ...(source === undefined ? {} : { source }) };
  const narrowed = withSeason ? scope : { ...scope, season: null };
  const empty = filteredEmpty(narrowed);
  return { missingKey: empty.key, missingValues: empty.values };
}
